import React, { useMemo } from "react";
import PropTypes from "prop-types";

import { defaultPayoutCurve, PAYOUT_UNITS } from "../../../components/CustomWidget/payoutCurve";
import { companyToday } from "../../../helpers/companyTime";
import { UseSelectedWorkspace } from "../../../hooks/company";
import { hasMetricPlan } from "../../../helpers/metricPlan";
import { MetricPicker, metricsForWorkspaces } from "./MetricPicker";
import MetricPlanEditor from "./MetricPlanEditor";

/**
 * Setup for Projected Annual Compensation.
 *
 * Live Q lines use metric columns + an auto default payout curve. Typed
 * overrides only appear when Live is switched off — no fallback required.
 */

const Field = ({ id, label, hint, value, onChange, step = 1000 }) => (
  <div className="gmbw-fld">
    <label htmlFor={id}>{label}</label>
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ color: "#98A2B3", fontSize: 13 }}>$</span>
      <input
        id={id}
        type="number"
        min="0"
        step={step}
        value={value != null && value !== "" ? value : ""}
        onChange={(e) => {
          const n = Number(e.target.value);
          onChange(Number.isFinite(n) && n >= 0 ? n : null);
        }}
      />
    </div>
    {hint ? <p className="gmbw-hint">{hint}</p> : null}
  </div>
);

Field.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  hint: PropTypes.node,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  step: PropTypes.number,
};
Field.defaultProps = {
  hint: null,
  value: "",
  step: 1000,
};

const Toggle = ({ label, hint, on, onToggle }) => (
  <div className="gmbw-fld">
    <div className="gmbw-bonusbar">
      <label style={{ margin: 0 }}>{label}</label>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        className={`gmbw-switch${on ? " on" : ""}`}
        onClick={onToggle}
      >
        <span className="gmbw-switch-dot" />
      </button>
    </div>
    {hint ? <p className="gmbw-hint">{hint}</p> : null}
  </div>
);

Toggle.propTypes = {
  label: PropTypes.string.isRequired,
  hint: PropTypes.node,
  on: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};
Toggle.defaultProps = { hint: null };

const AnnualCompSetup = ({ draft, onChange, metricCatalog = [] }) => {
  const set = (key, val) => onChange({ ...draft, [key]: val });
  const d = draft || {};
  const selected = UseSelectedWorkspace();
  const q = companyToday().quarter();
  const priorLabel = q <= 1 ? "Prior quarters (none yet in Q1)" : `Q1–Q${q - 1} performance bonuses`;
  const currentLabel = `Q${q} performance bonus (on pace)`;

  const wsIds = (d.workspaceIds || []).filter(Boolean).map(String);
  const multiWs = wsIds.length >= 2;
  const planOn = hasMetricPlan(d.metricPlan);
  const planCols = planOn
    ? (d.metricPlan.cols || []).filter((c) => c && c.show !== false)
    : [];
  const moneyCols = planCols.filter((c) => c.kind === "$");
  const selectableCols = moneyCols.length ? moneyCols : planCols;

  const pickedKeys = useMemo(() => {
    const stored = Array.isArray(d.revenueColumnKeys)
      ? d.revenueColumnKeys.map(String).filter(Boolean)
      : [];
    if (stored.length) return stored.filter((k) => selectableCols.some((c) => String(c.key) === k));
    return selectableCols.map((c) => String(c.key));
  }, [d.revenueColumnKeys, selectableCols]);

  const toggleCol = (key) => {
    const k = String(key);
    const on = pickedKeys.includes(k);
    const next = on ? pickedKeys.filter((x) => x !== k) : [...pickedKeys, k];
    set("revenueColumnKeys", next);
  };

  const scopedMetrics = useMemo(
    () => metricsForWorkspaces(metricCatalog, d.workspaceIds || [], selected?._id),
    [metricCatalog, d.workspaceIds, selected?._id],
  );
  const revenueMetrics = useMemo(() => {
    const money = scopedMetrics.filter((m) => m.kind === "$");
    return money.length ? money : scopedMetrics;
  }, [scopedMetrics]);

  /* Curve/target default on the card at paint time — do NOT auto-write them
     here. A useEffect that spread a stale draft was wiping metricPlan /
     workspaceIds on Save. */

  const curveOn = !!(d.payoutCurve && d.payoutCurve.on);
  const tiers = (d.payoutCurve && d.payoutCurve.tiers) || [];
  const setCurve = (pc) => onChange({ ...draft, payoutCurve: pc });
  const setTiers = (next) => setCurve({ on: true, tiers: next });
  const editTier = (i, patch) => setTiers(tiers.map((t, x) => (x === i ? { ...t, ...patch } : t)));
  const addTier = () => {
    const maxAt = tiers.reduce((m, t) => Math.max(m, Number(t.at) || 0), 0);
    const maxPay = tiers.reduce((m, t) => Math.max(m, Number(t.pay) || 0), 0);
    const unit = tiers.length ? tiers[tiers.length - 1].unit : "%";
    setTiers([...tiers, { at: unit === "%" ? maxAt + 10 : maxAt + 50000, unit, pay: maxPay + 2500 }]);
  };
  const removeTier = (i) => setTiers(tiers.filter((_, x) => x !== i));

  const livePriorOn = d.useLivePrior !== false;
  const liveCurrentOn = d.useLiveCurrent !== false;
  const liveKickerOn = d.useLiveKicker !== false;
  const priorManualDefault = q <= 1 ? 0 : 40000;

  const pickMetric = (m) => onChange({
    ...draft,
    revenueMetricId: m.id,
    revenueMetricLabel: m.label || "",
    revenueMetricIds: Array.isArray(m.ids) ? m.ids.map(String) : [],
    payoutCurve: (d.payoutCurve && d.payoutCurve.on) ? d.payoutCurve : defaultPayoutCurve(),
    goalTarget: Number(d.goalTarget) > 0 ? d.goalTarget : 1000000,
  });

  return (
    <>
      <p className="gmbw-hint" style={{ marginTop: 0 }}>
        Scope <b>two or more teams</b>, map metrics like Team Performance vs. The Yeti, tick the
        revenue columns — live Q lines compute from those. No typed fallback while Live is on.
      </p>

      {multiWs ? (
        <>
          <MetricPlanEditor draft={draft} onChange={onChange} minSeedCols={2} metricCatalog={metricCatalog} />

          {selectableCols.length > 0 && (
            <div className="gmbw-fld">
              <label>Columns for live quarter bonuses</label>
              <p className="gmbw-hint">
                Tick every revenue column that should feed Q1…Q{q} bonus math. Two or more are summed, then run through the payout curve.
              </p>
              <div className="gmbw-showlist">
                {selectableCols.map((col) => {
                  const key = String(col.key);
                  const on = pickedKeys.includes(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`gmbw-show${on ? " on" : ""}`}
                      aria-pressed={on}
                      onClick={() => toggleCol(key)}
                    >
                      <span className="gmbw-show-rad" />
                      <span className="gmbw-show-t">
                        <b>{col.label || key}</b>
                        <span>
                          {col.kind || "#"}
                          {" · "}
                          {col.mode === "sum" ? "sum" : "average"}
                          {" across workspaces"}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
              {pickedKeys.length < 1 && (
                <p className="gmbw-hint" style={{ color: "#F04438" }}>
                  Select at least one column.
                </p>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="gmbw-fld">
          <label>Revenue metric (for live quarter bonuses)</label>
          <p className="gmbw-hint" style={{ color: "#B54708" }}>
            Pick <b>two or more workspaces</b> under Basics for the full Metrics plan. Until then, choose one metric here.
          </p>
          <MetricPicker
            metrics={revenueMetrics}
            value={d.revenueMetricId || null}
            onPick={pickMetric}
          />
        </div>
      )}

      <Field
        id="pac-base"
        label="Base salary (annualized)"
        hint="Static for now — type the role’s salary."
        value={d.baseSalary != null ? d.baseSalary : 100000}
        onChange={(v) => set("baseSalary", v != null ? v : 100000)}
      />

      <Field
        id="pac-goal"
        label="Quarterly bonus target (revenue)"
        hint="“Of $X” for % tiers. Defaults to $1,000,000 if left blank."
        value={d.goalTarget != null ? d.goalTarget : 1000000}
        onChange={(v) => set("goalTarget", v != null && v > 0 ? v : 1000000)}
      />

      <div className="gmbw-fld">
        <div className="gmbw-bonusbar">
          <label style={{ margin: 0 }}>Payout curve (edit tiers)</label>
          <button
            type="button"
            role="switch"
            aria-checked={curveOn}
            aria-label="Payout curve"
            className={`gmbw-switch${curveOn ? " on" : ""}`}
            onClick={() => setCurve(curveOn ? null : defaultPayoutCurve())}
          >
            <span className="gmbw-switch-dot" />
          </button>
        </div>
        <p className="gmbw-hint">
          Live always has a curve. Turn this on to edit tiers; the card uses stock tiers until you customize.
        </p>
        {curveOn && (
          <div className="gmbw-pc" style={{ marginTop: 10 }}>
            {tiers.map((t, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div className="gmbw-pc-row" key={i}>
                <div className="gmbw-pc-row-main">
                  <span className="gmbw-pc-lbl">At</span>
                  <input
                    className="gmbw-pc-in gmbw-pc-at"
                    type="number"
                    min="0"
                    value={t.at ?? ""}
                    onChange={(e) => editTier(i, { at: Number(e.target.value) })}
                  />
                  <select
                    className="gmbw-pc-unit"
                    value={t.unit || "%"}
                    onChange={(e) => editTier(i, { unit: e.target.value })}
                  >
                    {PAYOUT_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                  <span className="gmbw-pc-lbl">pay</span>
                  <span className="gmbw-pc-dollar">$</span>
                  <input
                    className="gmbw-pc-in gmbw-pc-pay"
                    type="number"
                    min="0"
                    step="25"
                    value={t.pay ?? ""}
                    onChange={(e) => editTier(i, { pay: Number(e.target.value) })}
                  />
                </div>
                <button type="button" className="gmbw-pc-rm" onClick={() => removeTier(i)}>
                  Remove tier
                </button>
              </div>
            ))}
            <button type="button" className="gmbw-pc-add" onClick={addTier}>+ Add a tier</button>
          </div>
        )}
      </div>

      <Toggle
        label={`Live ${priorLabel}`}
        hint="Completed quarters × chosen columns × payout curve."
        on={livePriorOn}
        onToggle={() => set("useLivePrior", !livePriorOn)}
      />
      {!livePriorOn && (
        <Field
          id="pac-prior"
          label={priorLabel}
          hint="Manual amount — only used while Live is off."
          value={d.priorBonuses != null ? d.priorBonuses : priorManualDefault}
          onChange={(v) => set("priorBonuses", v != null ? v : 0)}
        />
      )}

      <Toggle
        label={`Live ${currentLabel}`}
        hint="This quarter’s chosen columns through the payout curve."
        on={liveCurrentOn}
        onToggle={() => set("useLiveCurrent", !liveCurrentOn)}
      />
      {!liveCurrentOn && (
        <Field
          id="pac-current"
          label={currentLabel}
          hint="Manual amount — only used while Live is off."
          value={d.q4Pace != null ? d.q4Pace : 15625}
          onChange={(v) => set("q4Pace", v != null ? v : 15625)}
          step={125}
        />
      )}

      <Toggle
        label="Live annual growth kicker"
        hint="Banked milestone money from the ladder on scoped workspaces (YTD)."
        on={liveKickerOn}
        onToggle={() => set("useLiveKicker", !liveKickerOn)}
      />
      {!liveKickerOn && (
        <Field
          id="pac-kicker"
          label="Annual growth kicker"
          hint="Manual amount — only used while Live is off."
          value={d.kickerEarned != null ? d.kickerEarned : 5000}
          onChange={(v) => set("kickerEarned", v != null ? v : 5000)}
        />
      )}
    </>
  );
};

AnnualCompSetup.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  draft: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  metricCatalog: PropTypes.array,
};
AnnualCompSetup.defaultProps = {
  metricCatalog: [],
};

export default AnnualCompSetup;
